import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Home-popup overlay state. Pairs with the chromed `/` Home route and
 * the sidebar's WorkFocus logo:
 *   • Sidebar Home nav item → navigates to `/` (shows the page with
 *     the sidebar + top bar around it).
 *   • Sidebar logo click → calls `open()` here, which raises the
 *     overlay Dialog rendered by `<HomePopup/>` inside `IframeShell`.
 *     No navigation — the operator stays on whatever page they were
 *     viewing.
 *
 * Operator-reported follow-up: "when clicking on home menu item,
 * user should see home page with left menu / and when user click on
 * logo - home page content should show as a popup without switching
 * a page".
 *
 * Provider mounts inside `<AuthLayout>` so both NavSidebar (logo
 * trigger) and IframeShell (Dialog host) consume the same state. The
 * popup uses the same `<HomeContentCard variant="popup">` content as
 * the chromed page, so the surfaces never diverge.
 */
export interface HomePopupContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const HomePopupContext = createContext<HomePopupContextValue | null>(null);

export function HomePopupProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const value = useMemo<HomePopupContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );
  return <HomePopupContext.Provider value={value}>{children}</HomePopupContext.Provider>;
}

export function useHomePopup(): HomePopupContextValue {
  const ctx = useContext(HomePopupContext);
  if (!ctx) {
    throw new Error('useHomePopup must be used within <HomePopupProvider>');
  }
  return ctx;
}
